import React, { useState, useEffect } from 'react';
import { Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { BareThemeProvider } from './components/design-system/theme';
import { CssBaseline } from '@mui/material';
import { authAPI } from './services/api';

// Contexts
import { OrgProvider } from './contexts/OrgContext';

// Public Pages  
import SplashPage from './components/splash/LaunchReadySplashPage';
import ShopLanding from './pages/ShopLanding';
import FigmaProductDetail from './pages/FigmaProductDetail';
import UnifiedLogin from './pages/auth/UnifiedLogin';
import PasswordlessLogin from './pages/auth/PasswordlessLogin';
import Signup from './pages/auth/Signup';
import PendingApproval from './pages/auth/PendingApproval';
import OrgSelect from './pages/OrgSelect';
import AdminDashboard from './pages/admin/AdminDashboard';

// Navigation Components
import InternalNavigation from './components/InternalNavigation';
import ClientNavigation from './components/ClientNavigation';
import RepNavigation from './components/RepNavigation';

// Components
import ProtectedRoute from './components/ProtectedRoute';
import FeatureGatedRoute from './components/FeatureGatedRoute';

// Authentication  
// import Login from './pages/auth/Login';

// Internal Pages
import BareDashboard from './pages/internal/BareDashboard';
import EPLDashboard from './pages/internal/EPLDashboard';
import PurchaseOrders from './pages/internal/PurchaseOrders';
import ReceiveInventoryWorkflow from './pages/internal/ReceiveInventoryWorkflow';
import ReceiveInventoryBatchDetail from './pages/internal/ReceiveInventoryBatchDetail';
import RawMaterials from './pages/internal/RawMaterials';
import ClientManagement from './pages/internal/ClientManagement';
import ClientAnalytics from './pages/internal/ClientAnalytics';
import OrderProcessing from './pages/internal/OrderProcessing';
import InventoryTracker from './pages/internal/InventoryTracker';
import InventoryManagement from './pages/internal/InventoryManagement';
import GlobalInventoryAudit from './pages/internal/GlobalInventoryAudit';
import InternalSettings from './pages/internal/Settings';
import CrmDashboard from './pages/internal/CrmDashboard';
import CompanyDetail from './pages/internal/CompanyDetail';
import CompanyManagement from './pages/internal/CompanyManagement';
import AdvancedAnalytics from './pages/internal/AdvancedAnalytics';

// Client Pages
import ClientDashboard from './pages/client/Dashboard';
import PlaceOrderNew from './pages/client/PlaceOrderNew';
import PlaceOrderLipoderma from './pages/client/PlaceOrderLipoderma';
import Checkout from './pages/client/Checkout';
import OrderHistory from './pages/client/OrderHistory';
import ClientInventoryManagement from './pages/client/InventoryManagement';
import ShippingLocations from './pages/client/ShippingLocations';
// import StorageManagement from './pages/client/StorageManagement';
// import ProductCatalog from './pages/client/ProductCatalog';
// import ClientProductDetail from './pages/client/ClientProductDetail';
// import PatientManagement from './pages/client/PatientManagement'; // Removed - no longer used
// import Reports from './pages/client/Reports';
// import Training from './pages/client/Training';
import ClientSettings from './pages/client/Settings';
import Payments from './pages/client/Payments';
import MyPayments from './pages/client/MyPayments';
import PayInvoice from './pages/client/PayInvoice';
import PayInvoicePartial from './pages/client/PayInvoicePartial';
import MyShippingAddress from './pages/client/MyShippingAddress';
import ClientPricing from './pages/client/ClientPricing';
import Confirmation from './pages/client/Confirmation';
import PaymentSuccess from './pages/client/PaymentSuccess';
import Resources from './pages/client/Resources';

// Rep Pages
import RepDashboard from './pages/rep/Dashboard';
import RepSales from './pages/rep/Sales';
import RepCommission from './pages/rep/Commission';
import RepTrends from './pages/rep/Trends';

function App() {
  const [currentUser, setCurrentUser] = useState(null);
  const [isSessionLoading, setIsSessionLoading] = useState(true);
  const navigate = useNavigate();

  // Load user from sessionStorage on mount (persist on refresh, clear on browser close)
  useEffect(() => {
    const savedUser = sessionStorage.getItem('barebiologics_currentUser');
    if (savedUser) {
      try {
        const user = JSON.parse(savedUser);
        setCurrentUser(user);
        
        // Check if authentication tokens exist
        const authToken = localStorage.getItem('authToken');
        const refreshToken = localStorage.getItem('refreshToken');
        
        if (!authToken || !refreshToken) {
          console.log('⚠️ User found but tokens missing, clearing user session');
          sessionStorage.removeItem('barebiologics_currentUser');
          setCurrentUser(null);
        } else {
          console.log('✅ User and tokens restored successfully');
        }
      } catch (e) {
        console.error('Error parsing saved user:', e);
        sessionStorage.removeItem('barebiologics_currentUser');
      }
    }
    setIsSessionLoading(false);
  }, []);

  // Idle-based sliding session refresh: refresh shortly before expiry if user is active
  useEffect(() => {
    let lastActivity = Date.now();
    const updateActivity = () => { lastActivity = Date.now(); };
    const activityEvents = ['mousemove', 'keydown', 'click', 'scroll', 'touchstart'];
    activityEvents.forEach(evt => window.addEventListener(evt, updateActivity));

    const checkInterval = setInterval(async () => {
      try {
        const accessToken = localStorage.getItem('authToken');
        const refreshToken = localStorage.getItem('refreshToken');
        if (!accessToken || !refreshToken) return;

        const parts = accessToken.split('.');
        if (parts.length !== 3) return;
        let payload;
        try {
          payload = JSON.parse(atob(parts[1]));
        } catch {
          return;
        }
        const exp = payload?.exp;
        if (!exp) return;

        const nowSec = Date.now() / 1000;
        const timeLeftSec = exp - nowSec;
        const idleMs = Date.now() - lastActivity;

        // Refresh if <60s left and user was active within last 5 minutes
        if (timeLeftSec <= 60 && idleMs < 5 * 60 * 1000) {
          const res = await authAPI.refreshToken();
          const newAccessToken = res?.accessToken || res?.token;
          if (newAccessToken) {
            localStorage.setItem('authToken', newAccessToken);
          }
        }
      } catch {
        // Silent fail; next cycle will retry
      }
    }, 30000);

    return () => {
      clearInterval(checkInterval);
      activityEvents.forEach(evt => window.removeEventListener(evt, updateActivity));
    };
  }, []);

  const handleLogin = (user) => {
    setCurrentUser(user);
    sessionStorage.setItem('barebiologics_currentUser', JSON.stringify(user));
    // Also store in localStorage for API auth (new)
    localStorage.setItem('currentUser', JSON.stringify(user));
    console.log('✅ User logged in and stored in both sessionStorage and localStorage');
  };

  const handleLogout = () => {
    setCurrentUser(null);
    sessionStorage.removeItem('barebiologics_currentUser');
    localStorage.removeItem('barebiologics_org');
    // Clear auth tokens
    localStorage.removeItem('authToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('currentUser');
    console.log('✅ User logged out - all tokens cleared');
    
    // Redirect to login page
    navigate('/login');
  };

  const handleOrderSubmit = (order) => {
    // setClientOrders(prev => [...prev, order]);
    console.log('Order submitted:', order);
  };

  return (
    <OrgProvider>
      <BareThemeProvider>
        <CssBaseline />
        
        <Routes>
          {/* Public routes */}
          <Route path="/" element={!currentUser ? <SplashPage /> : <Navigate to={currentUser.userType === 'internal' ? '/org-select' : `/${currentUser.userType}/dashboard`} replace />} />
          <Route path="/login" element={!currentUser ? <PasswordlessLogin onLogin={handleLogin} /> : <Navigate to={currentUser.userType === 'internal' ? '/org-select' : `/${currentUser.userType}/dashboard`} replace />} />
          <Route path="/signup" element={!currentUser ? <Signup /> : <Navigate to={currentUser.userType === 'internal' ? '/org-select' : `/${currentUser.userType}/dashboard`} replace />} />
          <Route path="/signup/pending" element={<PendingApproval />} />
          <Route 
            path="/admin" 
            element={
              currentUser?.isSuperAdmin ? <AdminDashboard currentUser={currentUser} /> : <Navigate to="/" replace />
            }
          />
          <Route path="/products" element={<ShopLanding />} />
          <Route path="/products/:productType" element={<FigmaProductDetail />} />
          <Route path="/insights" element={<SplashPage />} />
          
          {/* Org selection for internal users */}
          <Route 
            path="/org-select" 
            element={
              <ProtectedRoute requiredUserType="internal" currentUser={currentUser} isLoading={isSessionLoading}>
                {(() => {
                  if (isSessionLoading) {
                    return <div style={{ padding: 16 }} />;
                  }

                  // If user missing, ProtectedRoute will redirect
                  const isSuperAdmin = currentUser?.isSuperAdmin === true;
                  const orgRaw = currentUser?.organization;
                  const normalizedOrg = typeof orgRaw === 'string' ? orgRaw.toLowerCase() : undefined;
                  const isBoth = typeof orgRaw === 'string' && orgRaw.toUpperCase() === 'BOTH';

                  if (isSuperAdmin || isBoth || !normalizedOrg) {
                    return <OrgSelect currentUser={currentUser} />;
                  }

                  if (normalizedOrg === 'bare' || normalizedOrg === 'epl') {
                    return <Navigate to={`/internal/${normalizedOrg}/dashboard`} replace />;
                  }

                  // Unknown org -> safer to show selector
                  return <OrgSelect currentUser={currentUser} />;
                })()}
              </ProtectedRoute>
            } 
          />
          
          {/* Internal routes with org requirement */}
          <Route 
            path="/internal/*" 
            element={
              <ProtectedRoute 
                requiredUserType="internal" 
                requireOrg={true} 
                currentUser={currentUser}
              >
                <InternalNavigation currentUser={currentUser} onLogout={handleLogout}>
                  <Routes>
                    {/* Bare Biologics routes */}
                    <Route path="bare/dashboard" element={<BareDashboard />} />
                    <Route path="bare/purchase-orders" element={<PurchaseOrders currentCompany="bare" />} />
                    <Route path="bare/receive-inventory" element={<ReceiveInventoryWorkflow currentCompany="bare" />} />
                    <Route path="bare/receive-inventory/batches/:batchId" element={<ReceiveInventoryBatchDetail currentCompany="bare" />} />
                    {/* <Route path="bare/raw-materials" element={<RawMaterials />} /> */}
                    <Route path="bare/order-processing" element={<OrderProcessing />} />
                    <Route path="bare/inventory-tracker" element={<InventoryTracker />} />
                    <Route path="bare/inventory-management" element={<InventoryManagement />} />
                    <Route path="bare/client-management" element={<ClientManagement />} />
                    <Route path="bare/client-analytics" element={<ClientAnalytics />} />
                    <Route path="bare/companies" element={<CompanyManagement />} />
                    <Route path="bare/analytics" element={<AdvancedAnalytics />} />
                    <Route path="bare/crm" element={<CrmDashboard />} />
                    <Route path="bare/companies/:companyId/*" element={<CompanyDetail />} />
                    <Route path="bare/settings" element={<InternalSettings />} />
                    <Route path="bare/admin/global-inventory-audit" element={<GlobalInventoryAudit />} />
                    
                    {/* EPL routes - Purchase Orders now enabled */}
                    <Route path="epl/dashboard" element={<EPLDashboard />} />
                    <Route path="epl/purchase-orders" element={<PurchaseOrders currentCompany="epl" />} />
                    <Route path="epl/receive-inventory" element={<ReceiveInventoryWorkflow currentCompany="epl" />} />
                    <Route path="epl/receive-inventory/batches/:batchId" element={<ReceiveInventoryBatchDetail currentCompany="epl" />} />
                    {/* <Route path="epl/raw-materials" element={<RawMaterials />} /> */}
                    <Route path="epl/order-processing" element={<OrderProcessing />} />
                    <Route path="epl/inventory-tracker" element={<InventoryTracker />} />
                    <Route path="epl/inventory-management" element={<InventoryManagement />} />
                    <Route path="epl/client-management" element={<ClientManagement />} />
                    <Route path="epl/client-analytics" element={<ClientAnalytics />} />
                    <Route path="epl/companies" element={<CompanyManagement />} />
                    <Route path="epl/analytics" element={<AdvancedAnalytics />} />
                    <Route path="epl/crm" element={<CrmDashboard />} />
                    <Route path="epl/companies/:companyId/*" element={<CompanyDetail />} />
                    <Route path="epl/settings" element={<InternalSettings />} />
                    <Route path="epl/admin/global-inventory-audit" element={<GlobalInventoryAudit />} />
                    
                    {/* Default redirect */}
                    <Route path="*" element={<Navigate to="/org-select" replace />} />
                  </Routes>
                </InternalNavigation>
              </ProtectedRoute>
            } 
          />
          
          {/* Client routes */}
          <Route 
            path="/client/*" 
            element={
              currentUser?.userType === 'client' ? (
                <ClientNavigation currentUser={currentUser} onLogout={handleLogout}>
                  <Routes>
                    <Route path="dashboard" element={<ClientDashboard currentUser={currentUser} />} />
                    <Route path="pricing/:product" element={<ClientPricing />} />
                    <Route 
                      path="place-order" 
                      element={
                        currentUser?.organization === 'BARE' 
                          ? <PlaceOrderLipoderma currentUser={currentUser} onOrderSubmit={handleOrderSubmit} />
                          : <PlaceOrderNew currentUser={currentUser} onOrderSubmit={handleOrderSubmit} />
                      } 
                    />
                    <Route path="checkout" element={<Checkout currentUser={currentUser} />} />
                    <Route path="orders" element={<OrderHistory currentUser={currentUser} />} />
                    <Route path="inventory-management" element={<ClientInventoryManagement currentUser={currentUser} />} />
                    <Route path="payments" element={<Payments />} />
                    <Route path="my-payments" element={<MyPayments currentUser={currentUser} />} />
                    <Route path="pay-invoice" element={<PayInvoicePartial currentUser={currentUser} />} />
                    <Route path="my-shipping-address" element={<MyShippingAddress currentUser={currentUser} />} />
                    <Route path="shipping-locations" element={<ShippingLocations currentUser={currentUser} />} />
                    <Route path="resources" element={<Resources />} />
                    {/* <Route path="storage" element={<StorageManagement currentUser={currentUser} />} /> */}
                    {/* <Route path="patients" element={<PatientManagement currentUser={currentUser} />} /> */}
                    {/* <Route path="reports" element={<Reports currentUser={currentUser} />} /> */}
                    {/* <Route path="training" element={<Training />} /> */}
                    <Route path="settings" element={<ClientSettings currentUser={currentUser} />} />
                    <Route path="confirmation" element={<Confirmation />} />
                    <Route path="payment-success" element={<PaymentSuccess />} />
                    <Route path="*" element={<Navigate to="/client/dashboard" replace />} />
                  </Routes>
                </ClientNavigation>
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
          
          {/* Rep routes */}
          <Route 
            path="/rep/*" 
            element={
              currentUser?.userType === 'rep' ? (
                <RepNavigation currentUser={currentUser} onLogout={handleLogout}>
                  <Routes>
                    <Route path="dashboard" element={<RepDashboard currentUser={currentUser} />} />
                    <Route path="sales" element={<RepSales currentUser={currentUser} />} />
                    <Route path="commission" element={<RepCommission currentUser={currentUser} />} />
                    <Route path="trends" element={<RepTrends currentUser={currentUser} />} />
                    <Route path="*" element={<Navigate to="/rep/dashboard" replace />} />
                  </Routes>
                </RepNavigation>
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
          
          {/* Fallback */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        
      </BareThemeProvider>
    </OrgProvider>
  );
}

export default App; 
